import { useQuery } from '@tanstack/react-query';
import { Bell, BarChart3, Cable, Cpu, Palette, Radio } from 'lucide-react';
import { useEffect, useState, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, NavLink, useParams } from 'react-router';
import { can, canAny, fetchInfo, type Me } from '../../api/client';
import { useMe } from '../auth/session';
import { BrandingCard } from './BrandingCard';
import { ProtocolsCard } from './ProtocolsCard';
import { AlarmTypesSettings } from './AlarmTypesSettings';
import { IntegrationsSettings } from './IntegrationsSettings';
import { OperationsSettingsCard } from './OperationsSettingsCard';
import { usePrefs, type DateFormat } from '../../app/prefs';
import type { Locale } from '../../i18n';
import { Card, PageHeader, Segmented } from '../../ui/components';
import { formatDateTime } from '../../ui/format';

/** Current time, ticking every second, for the live date-format example. */
function useNowIso() {
  const [now, setNow] = useState(() => new Date().toISOString());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date().toISOString()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

/**
 * Settings in tabs (user request 2026-10-08): the tab is part of the URL (/settings/<tab>), so it can be linked and
 * survives a refresh; tabs the user may not use are not shown, and an unknown or forbidden tab falls back to General.
 */
const TABS: {
  key: string;
  icon: ComponentType<{ size?: number }>;
  visible: (me: Me | null | undefined) => boolean;
}[] = [
  { key: 'general', icon: Palette, visible: () => true },
  { key: 'communication', icon: Radio, visible: (me) => can(me, 'settings.manage') },
  // the client users (site administrator, operator) keep General only, like the authority observer (user, 2026-10-10)
  { key: 'alarms', icon: Bell, visible: (me) => canAny(me, 'alarm.configure') },
  // retention and automatic reports are platform settings: superadmin only (user, 2026-10-09)
  { key: 'reports', icon: BarChart3, visible: (me) => can(me, 'settings.manage') },
  {
    key: 'integrations',
    icon: Cable,
    visible: (me) => canAny(me, 'integration.manage') || canAny(me, 'device.inspect'),
  },
  // the authority observer (2026-10-09) and the client users (2026-10-10) keep General only
  { key: 'system', icon: Cpu, visible: (me) => canAny(me, 'device.inspect') },
];

export function SettingsPage() {
  const { t } = useTranslation();
  const { tab = 'general' } = useParams();
  const me = useMe().data;
  const visible = TABS.filter((x) => x.visible(me));
  if (me && !visible.some((x) => x.key === tab)) return <Navigate to="/settings/general" replace />;
  return (
    <div className="page" style={{ maxWidth: 1180 }}>
      <PageHeader title={t('settings.title')} />
      <nav className="tabs" aria-label={t('settings.title')}>
        {visible.map((x) => (
          <NavLink key={x.key} to={`/settings/${x.key}`}>
            <x.icon size={15} />
            {t(`opsSettings.tabs.${x.key}` as 'opsSettings.tabs.general')}
          </NavLink>
        ))}
      </nav>
      {tab === 'general' && (
        <div className="ops-stack">
          <AppearanceCard />
          {can(me, 'settings.manage') && <BrandingCard />}
        </div>
      )}
      {tab === 'communication' && <ProtocolsCard />}
      {tab === 'alarms' && <AlarmTypesSettings />}
      {tab === 'reports' && <OperationsSettingsCard section="reports" />}
      {tab === 'integrations' && <IntegrationsSettings />}
      {tab === 'system' && (
        <div className="ops-stack">
          {can(me, 'settings.manage') && <OperationsSettingsCard section="system" />}
          <AboutCard />
        </div>
      )}
    </div>
  );
}

function AppearanceCard() {
  const { t } = useTranslation();
  const { theme, locale, dateFormat, setTheme, setLocale, setDateFormat } = usePrefs();
  const now = useNowIso();
  return (
    <Card title={t('settings.appearance')}>
      <dl className="kv" style={{ alignItems: 'center' }}>
        <dt>{t('settings.theme')}</dt>
        <dd>
          <Segmented
            label={t('settings.theme')}
            value={theme}
            onChange={setTheme}
            options={[
              { value: 'light', label: t('settings.light') },
              { value: 'dark', label: t('settings.dark') },
            ]}
          />
        </dd>
        <dt>{t('settings.language')}</dt>
        <dd>
          <Segmented<Locale>
            label={t('settings.language')}
            value={locale}
            onChange={setLocale}
            options={[
              { value: 'en', label: 'English' },
              { value: 'ro', label: 'Română' },
            ]}
          />
        </dd>
        <dt>{t('settings.dateFormat')}</dt>
        <dd>
          <Segmented<DateFormat>
            label={t('settings.dateFormat')}
            value={dateFormat}
            onChange={setDateFormat}
            options={[
              { value: 'auto', label: t('settings.dateFormatAuto') },
              { value: 'dd-mm-yyyy', label: 'DD-MM-YYYY' },
              { value: 'yyyy-mm-dd', label: 'YYYY-MM-DD' },
            ]}
          />
          <div className="muted small" style={{ marginTop: 'var(--space-1)' }}>
            {t('settings.dateFormatExample', { value: formatDateTime(now) })}
          </div>
        </dd>
      </dl>
      <p className="muted" style={{ marginBottom: 0 }}>
        {t('settings.dateFormatHint')}
      </p>
      <p className="muted" style={{ marginBottom: 0 }}>
        {t('settings.languageHint')}
      </p>
    </Card>
  );
}

function AboutCard() {
  const { t } = useTranslation();
  const info = useQuery({ queryKey: ['info'], queryFn: fetchInfo, staleTime: Infinity, retry: 1 });
  return (
    <Card title={t('settings.about')}>
      <dl className="kv">
        <dt>UI</dt>
        <dd className="mono">{__APP_VERSION__}</dd>
        <dt>{t('realtime.backend')}</dt>
        <dd className="mono">{info.data?.version ?? (info.isError ? t('realtime.backendDown') : '…')}</dd>
        <dt>{t('settings.contracts')}</dt>
        <dd className="mono">{info.data ? Object.values(info.data.contracts).join(' · ') : '—'}</dd>
      </dl>
    </Card>
  );
}
